import { useMemo, useState } from "react";
import { FaCheck, FaCircle, FaPlay, FaStop } from "react-icons/fa";
import Input from "../../main/components/Input";
import { apiBaseUrl } from "../../main/components/trpc.ts";

interface RespondRecorderProps {
  ownerId: string;
  onUploaded: () => void;
}

export default function RespondRecorder(props: RespondRecorderProps) {
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioChunks, setAudioChunks] = useState<Blob[]>([]);
  const [name, setName] = useState("");
  const [isUploading, setIsUploading] = useState(false);

  const previewUrl = useMemo(() => {
    if (audioChunks.length === 0) {
      return null;
    }
    const blob = new Blob(audioChunks, { type: "audio/webm" });
    return URL.createObjectURL(blob);
  }, [audioChunks]);

  const handleRecordStart = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const nextRecorder = new MediaRecorder(stream);
    setMediaRecorder(nextRecorder);
    nextRecorder.start();
    setIsRecording(true);

    nextRecorder.ondataavailable = (event) => {
      setAudioChunks([event.data]);
    };

    nextRecorder.onstop = () => {
      setIsRecording(false);
      stream.getTracks().forEach((track) => track.stop());
    };
  };

  const handleRecordStop = () => {
    mediaRecorder?.stop();
  };

  const handleRecordPlay = () => {
    if (!previewUrl) {
      return;
    }
    const audio = new Audio(previewUrl);
    audio.play();
    setIsPlaying(true);
    audio.onended = () => {
      setIsPlaying(false);
    };
  };

  const handleUpload = async () => {
    const chunk = audioChunks[0];
    if (!chunk || !name.trim()) {
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("announce", chunk, "respond-announce.webm");
    formData.append("name", name.trim());
    formData.append("ownerId", props.ownerId);

    try {
      const response = await fetch(`${apiBaseUrl}/cadendarRest/uploadRespondAnnounce`, {
        method: "POST",
        body: formData,
        credentials: "include",
      });

      if (!response.ok) {
        throw new Error("Failed to upload announce");
      }

      setAudioChunks([]);
      setName("");
      props.onUploaded();
    } catch (error) {
      console.error("Failed to upload respond announce", error);
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <section className="rounded-[22px] bg-white/90 p-[22px] shadow-[0_24px_80px_rgba(15,23,42,0.08)]">
      <div className="text-base font-bold text-slate-900">
        Enregistrer une nouvelle annonce
      </div>

      <div className="mt-[14px] flex flex-wrap items-center gap-2.5">
        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border-none bg-slate-100 text-slate-900 cursor-pointer hover:bg-slate-200 disabled:cursor-default disabled:opacity-50"
          onClick={handleRecordStart}
          disabled={isRecording}
          title="Démarrer l'enregistrement"
          aria-label="Démarrer l'enregistrement"
        >
          <FaCircle className="h-4 w-4 text-red-600" />
        </button>
        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border-none bg-slate-100 text-slate-900 cursor-pointer hover:bg-slate-200 disabled:cursor-default disabled:opacity-50"
          onClick={handleRecordStop}
          disabled={!isRecording}
          title="Arrêter l'enregistrement"
          aria-label="Arrêter l'enregistrement"
        >
          <FaStop className="h-4 w-4" />
        </button>
        <button
          type="button"
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border-none bg-slate-100 text-slate-900 cursor-pointer hover:bg-slate-200 disabled:cursor-default disabled:opacity-50"
          onClick={handleRecordPlay}
          disabled={isRecording || audioChunks.length === 0}
          title="Écouter l'aperçu"
          aria-label="Écouter l'aperçu"
        >
          <FaPlay
            className={isPlaying ? "h-4 w-4 text-teal-700" : "h-4 w-4"}
          />
        </button>
        {isRecording ? (
          <span className="font-bold text-red-700">Enregistrement en cours...</span>
        ) : null}
      </div>

      {audioChunks.length > 0 ? (
        <div className="mt-[14px] grid grid-cols-[minmax(0,1fr)_auto] gap-2.5">
          <Input value={name} onChange={setName} placeholder="Nom de l'annonce" />
          <button
            type="button"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border-none bg-green-100 text-green-800 cursor-pointer hover:bg-green-200 disabled:cursor-default disabled:opacity-50"
            onClick={handleUpload}
            disabled={isUploading || !name.trim()}
            title="Enregistrer l'annonce"
            aria-label="Enregistrer l'annonce"
          >
            <FaCheck className="h-4 w-4" />
          </button>
        </div>
      ) : null}
    </section>
  );
}